import { useCallback, useEffect, useMemo, useRef, useState } from 'react';

import { useBxpWebStargateClient } from '@wac-web/libraries/bxp-web-stargate-client/src/hook/use-bxp-web-stargate-client';
import {
	type FastshiftDcProductType,
	type GetFastshiftOrganizationDcInstancesResponseData,
} from '@wac-web/libraries/bxp-web-stargate-client/src/hook/use-bxp-web-stargate-client.types';
import { type MessageDescriptor, useIntl } from 'react-intl';

import { LinkButton } from '@atlaskit/button/new';
import Heading from '@atlaskit/heading';
import Lozenge from '@atlaskit/lozenge';
import { Inline, Stack, Text } from '@atlaskit/primitives/compiled';

import { ActionSubjectIdKey } from '@/analytics/constants';
import { GenericErrorDisplay } from '@/common/displays/src/generic-error-display/generic-error-display';
import {
	FASTSHIFT_PRODUCT_VARIANTS,
	FastshiftProductIcon,
} from '@/common/fastshift-product-icon/src/fastshift-product-icon';
import {
	DEFAULT_CONTROLS_PAYLOAD,
	FastshiftTable,
	type ControlsPayload,
	type TableHeads,
	type TableRows,
} from '@/common/fastshift-table';
import { useRoutes } from '@/common/hooks/useRoutes';
import { FASTSHIFT_PATHS, FASTSHIFT_PATH_KEYS } from '@/common/routes/constants';
import { getAssessmentStatusConfig } from '@/common/utils/get-assessment-status-config';
import { getConnectionStatusConfig } from '@/common/utils/get-connection-status-config';
import { useFastshiftDispatch, useFastshiftSelector, type FastshiftDispatchPromise } from '@/state';
import { getFastshiftOrganizationDcInstances } from '@/state/actions';

import { useOrganizationDetailsSectionAnalytics } from '../hooks/use-organization-details-section-analytics';

import { messages } from './messages';

const PRODUCT_MESSAGE_BY_TYPE: Record<FastshiftDcProductType, MessageDescriptor> = {
	JIRA: messages.jiraProduct,
	CONFLUENCE: messages.confluenceProduct,
};

const getProductMessage = (productType: FastshiftDcProductType | null): MessageDescriptor =>
	(productType && PRODUCT_MESSAGE_BY_TYPE[productType]) ?? messages.unknownProduct;

const EMPTY_INSTANCES: GetFastshiftOrganizationDcInstancesResponseData = [];

interface OrganizationDcInstancesTableProps {
	domainId: string;
	orgId: string;
}

export const OrganizationDcInstancesTable = ({
	domainId,
	orgId,
}: OrganizationDcInstancesTableProps) => {
	const { formatMessage } = useIntl();
	const { getPathByDcInstanceId } = useRoutes();
	const stargateClient = useBxpWebStargateClient();
	const dispatch = useFastshiftDispatch();
	const activeRequestRef = useRef<FastshiftDispatchPromise<unknown> | null>(null);
	const [controls, setControls] = useState<ControlsPayload>(DEFAULT_CONTROLS_PAYLOAD);
	const {
		domainId: stateDomainId,
		orgId: stateOrgId,
		dcInstances,
	} = useFastshiftSelector((state) => state.organizationDetail);

	const loadInstances = useCallback(() => {
		activeRequestRef.current?.abort();
		activeRequestRef.current = dispatch(
			getFastshiftOrganizationDcInstances({
				stargateClient,
				domainId,
				orgId,
			}),
		);
	}, [dispatch, domainId, orgId, stargateClient]);

	useEffect(() => {
		setControls(DEFAULT_CONTROLS_PAYLOAD);
		loadInstances();

		return () => {
			activeRequestRef.current?.abort();
			activeRequestRef.current = null;
		};
	}, [loadInstances]);

	const hasMatchingOrg = stateDomainId === domainId && stateOrgId === orgId;
	const instances = (hasMatchingOrg ? dcInstances.data : null) ?? EMPTY_INSTANCES;
	const totalElements = instances.length;
	const isTableLoading =
		!hasMatchingOrg || dcInstances.pending || (dcInstances.data === null && !dcInstances.error);
	useOrganizationDetailsSectionAnalytics({
		organizationId: orgId,
		actionSubjectId: ActionSubjectIdKey.ORGANIZATION_DC_INSTANCES_TABLE,
		status: isTableLoading
			? null
			: dcInstances.error
				? 'error'
				: totalElements
					? 'normal'
					: 'empty',
	});
	const totalPages = Math.ceil(totalElements / controls.size);
	const visibleInstances = useMemo(() => {
		const firstVisibleIndex = controls.page * controls.size;

		return instances.slice(firstVisibleIndex, firstVisibleIndex + controls.size);
	}, [controls.page, controls.size, instances]);

	const head = useMemo<TableHeads>(() => {
		const cells: TableHeads['cells'] = [
			{
				key: 'source',
				content: (
					<Heading size="xxsmall" as="span">
						{formatMessage(messages.sourceColumnHeading)}
					</Heading>
				),
				width: 57,
			},
			{
				key: 'connection',
				content: (
					<Heading size="xxsmall" as="span">
						{formatMessage(messages.connectionColumnHeading)}
					</Heading>
				),
				width: 15,
			},
		];

		cells.push(
			{
				key: 'assessment-status',
				content: (
					<Heading size="xxsmall" as="span">
						{formatMessage(messages.assessmentStatusColumnHeading)}
					</Heading>
				),
				width: 15,
			},
			{
				key: 'actions',
				content: (
					<Inline alignInline="end">
						<Heading size="xxsmall" as="span">
							{formatMessage(messages.actionsColumnHeading)}
						</Heading>
					</Inline>
				),
				width: 13,
			},
		);

		return { cells };
	}, [formatMessage]);

	const rows = useMemo<TableRows>(
		() =>
			visibleInstances.map((instance) => {
				const connectionStatus = getConnectionStatusConfig(instance.status);
				const assessmentStatus = getAssessmentStatusConfig(instance.assessmentStatus);
				const productMessage = getProductMessage(instance.productType);
				const cells: TableRows[number]['cells'] = [
					{
						key: 'source',
						content: (
							<Inline space="space.100" alignBlock="center">
								<FastshiftProductIcon
									productName={instance.productType}
									variant={FASTSHIFT_PRODUCT_VARIANTS.SOURCE}
								/>
								<Stack space="space.025">
									<Text>{formatMessage(productMessage)}</Text>
									<Text color="color.text.subtle">{instance.baseUrl}</Text>
								</Stack>
							</Inline>
						),
					},
					{
						key: 'connection',
						content: (
							<Lozenge
								appearance={connectionStatus.appearance}
								testId={`organization-dc-instance-connection-${instance.linkId}`}
							>
								{formatMessage(connectionStatus.message)}
							</Lozenge>
						),
					},
				];

				cells.push(
					{
						key: 'assessment-status',
						content: (
							<Lozenge
								appearance={assessmentStatus.appearance}
								testId={`organization-dc-instance-assessment-${instance.linkId}`}
							>
								{formatMessage(assessmentStatus.message)}
							</Lozenge>
						),
					},
					{
						key: 'actions',
						content: (
							<Inline alignInline="end">
								{instance.detailsAvailable && instance.instanceId ? (
									<LinkButton
										href={getPathByDcInstanceId(
											FASTSHIFT_PATHS[FASTSHIFT_PATH_KEYS.DC_INSTANCE_DETAILS],
											instance.instanceId,
										)}
										spacing="compact"
									>
										{formatMessage(messages.viewDetails)}
									</LinkButton>
								) : null}
							</Inline>
						),
					},
				);

				return {
					key: instance.linkId,
					cells,
				};
			}),
		[formatMessage, getPathByDcInstanceId, visibleInstances],
	);

	return (
		<Stack space="space.100">
			<Heading size="small" as="h2">
				{formatMessage(messages.sectionHeading)}
			</Heading>
			<FastshiftTable
				id="organization-dc-instances"
				testId="organization-dc-instances"
				tableCaption={formatMessage(messages.tableCaption)}
				isTableLoading={isTableLoading}
				isTableError={hasMatchingOrg && Boolean(dcInstances.error)}
				errorComponent={
					<GenericErrorDisplay
						customMessage={formatMessage(messages.errorMessage)}
						onRefresh={loadInstances}
					/>
				}
				head={head}
				rows={rows}
				controls={{
					pagination: {
						page: controls.page,
						size: controls.size,
						totalElements,
						totalPages,
						hasNext: controls.page + 1 < totalPages,
						hasPrevious: controls.page > 0,
						sorted: false,
					},
					controls,
					onSetControlsPayload: setControls,
				}}
			/>
		</Stack>
	);
};
